import React, { forwardRef, ReactElement, useMemo, useState } from 'react'
import { Dropdown as ReactDropDown, DropdownProps as ReactDropDownProps } from 'primereact/dropdown'
import { Message } from 'primereact/message'
import cn from 'classnames'
import { Tooltip } from 'primereact/tooltip'

import useText from '../../hooks/useText'
import { FieldInfoButton } from '../field-info-button'

export interface DropdownProps extends ReactDropDownProps {
    id: string
    disabledReason?: string
    error?: string
    infoText?: string
    ['data-testid']?: string
    className?: string
    label?: string
    endButton?: ReactElement
}

export const Dropdown = forwardRef<ReactDropDown, DropdownProps>(
    ({ infoText, disabledReason, className, endButton, ...props }: DropdownProps, ref) => {
        const text = useText()

        const [filter, setFilter] = useState('')
        const rootClassName = cn('flex flex-column', className)

        const { processedOptions, filteredOptionsCount } = useMemo(() => {
            if (!props.options || props.options.length <= 1000) {
                return {
                    processedOptions: props.options,
                    filteredOptionsCount: props.options?.length ?? 0,
                }
            }

            const allOptions = props.options ?? []
            const filteredOptions = filter
                ? allOptions.filter((opt) => String(opt.label).toLowerCase().includes(filter.toLowerCase()))
                : allOptions
            const first1000 = filteredOptions.slice(0, 1000)

            if (props.value !== null) {
                const selectedOption = allOptions.find((opt) => opt.value === props.value)
                if (selectedOption && !first1000.some((opt) => opt.value === selectedOption.value)) {
                    return {
                        processedOptions: [selectedOption, ...first1000],
                        filteredOptionsCount: filteredOptions.length,
                    }
                }
            }

            return {
                processedOptions: first1000,
                filteredOptionsCount: filteredOptions.length,
            }
        }, [props.options, props.value, filter])

        // Auto-enable virtual scrolling for large datasets
        const virtualScrollerOptions = useMemo(() => {
            if (!props.options || props.options.length <= 1000) {
                return props.virtualScrollerOptions // No virtual scrolling for small datasets
            }

            if (filteredOptionsCount <= 1000) {
                return props.virtualScrollerOptions
            }

            return {
                itemSize: 38,
                showLoader: false,
                ...props.virtualScrollerOptions, // Allow override
            }
        }, [props.options, props.virtualScrollerOptions, filteredOptionsCount])

        // Force re-mount when options first load to fix PrimeReact layout calculation bug
        const optionsKey = props.options?.length ? 'loaded' : 'empty'

        const dropdownElement = (
            <ReactDropDown
                key={optionsKey}
                emptyMessage={text('msgNoResultsFound')}
                emptyFilterMessage={text('msgNoAvailableOptions')}
                showClear
                filter
                onFilter={(e) => setFilter(e.filter)}
                {...props}
                filterInputAutoFocus={props.filterInputAutoFocus ?? props.filter ?? true}
                options={processedOptions}
                virtualScrollerOptions={virtualScrollerOptions}
                pt={{
                    root: {
                        className: 'w-full',
                    },
                    input: {
                        'data-testid': `${props.id}-input`,
                    },
                    clearIcon: {
                        'data-testid': `${props.id}-clear-icon`,
                    },
                    panel: {
                        style: { width: '250px' },
                    },
                }}
                ref={ref}
                value={props.value}
                disabled={props.disabled}
                data-testid={props.disabled ? `${props.id}-disabled` : `${props.id}`}
                inputId={props.id}
            />
        )

        const dropdownWithEndButton = endButton ? (
            <div className={`p-inputgroup dropdown-group-${props.id}`}>
                {dropdownElement}
                {endButton}
            </div>
        ) : (
            dropdownElement
        )

        return (
            <div className={rootClassName}>
                {props.label || infoText ? (
                    <div className="flex justify-content-between">
                        {props.label ? (
                            <label className={cn({ 'required-label': props.required }, 'p-label')} htmlFor={props.id}>
                                {props.label}
                            </label>
                        ) : null}
                        {infoText ? (
                            <FieldInfoButton
                                ariaLabel={text('lblMoreInformation')}
                                text={infoText}
                                testId={`${props.id}-info-button`}
                            />
                        ) : null}
                    </div>
                ) : null}

                {dropdownWithEndButton}

                {disabledReason && (
                    <Tooltip
                        target={endButton ? `.dropdown-group-${props.id}` : `#${props.id}`}
                        className="w-2"
                        showOnDisabled
                        position="top"
                    >
                        {disabledReason}
                    </Tooltip>
                )}

                {props.error && (
                    <Message
                        severity="error"
                        text={props.error}
                        data-testid={`error-text:${props.error}`}
                        className="mt-2 justify-content-start"
                    />
                )}
            </div>
        )
    }
)
